import React, { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Modal as CoreModal } from "@sparefoot/react-commons";
import { Button } from "components/core/Button";
import { Cross } from "components/core/Icons";

import "./Modal.scss";

const MODAL_SIZES = {
	SMALL: "small",
	MEDIUM: "medium",
	LARGE: "large",
	FULLSCREEN: "fullscreen"
};

const CLOSE_BUTTON_TYPES = {
	ICON: "icon",
	TEXT: "text"
};

function Modal({
	children,
	className,
	closable,
	handleClose,
	isOpen,
	size,
	closeButtonType,
	closeButtonText,
	toolbarContent,
	ariaLabel
}) {
	const contentRef = useRef(null);

	const handleEscapeKey = useCallback(
		(e) => {
			if (isOpen && closable && e.key === "Escape") {
				handleClose();
			}
		},
		[isOpen, closable, handleClose]
	);

	useEffect(() => {
		if (isOpen) {
			document.querySelector("body").classList.add("modal-active");
			document.body.addEventListener("keydown", handleEscapeKey);

			// Focus the modal content for screen readers
			if (contentRef.current) {
				contentRef.current.focus();
			}
		} else {
			document.querySelector("body").classList.remove("modal-active");
		}

		return () => {
			document.body.removeEventListener("keydown", handleEscapeKey);
			document.querySelector("body").classList.remove("modal-active");
		};
	}, [isOpen, handleEscapeKey]);

	const renderCloseButton = () => {
		if (!closable) return null;

		if (closeButtonType === CLOSE_BUTTON_TYPES.TEXT) {
			return (
				<Button
					label="Close Modal"
					className="modal-close-btn modal-close-btn--text"
					onClick={handleClose}
					theme="text"
				>
					{closeButtonText}
				</Button>
			);
		}

		return (
			<Button
				label="Close Modal"
				className="modal-close-btn modal-close-btn--icon"
				onClick={handleClose}
				theme="icon"
			>
				<Cross fill="white" size="large" />
			</Button>
		);
	};

	return (
		<CoreModal
			data-testid="modal"
			className={classnames(
				"molecules-modal",
				`molecules-modal--${size}`,
				className
			)}
			closable={closable}
			isOpen={isOpen}
			handleClose={handleClose}
		>
			{toolbarContent && (
				<div className="molecules-modal__toolbar">
					<div className="molecules-modal__toolbar-content">
						{toolbarContent}
					</div>
					{renderCloseButton()}
				</div>
			)}
			<div
				className="molecules-modal__content"
				ref={contentRef}
				tabIndex={-1}
				role="dialog"
				aria-modal="true"
				aria-label={ariaLabel}
			>
				{!toolbarContent && renderCloseButton()}
				{children}
			</div>
		</CoreModal>
	);
}

Modal.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	closable: PropTypes.bool,
	handleClose: PropTypes.func.isRequired,
	isOpen: PropTypes.bool,
	size: PropTypes.oneOf(Object.values(MODAL_SIZES)),
	closeButtonType: PropTypes.oneOf(Object.values(CLOSE_BUTTON_TYPES)),
	closeButtonText: PropTypes.string,
	toolbarContent: PropTypes.node,
	ariaLabel: PropTypes.string
};

Modal.defaultProps = {
	closable: true,
	isOpen: false,
	size: MODAL_SIZES.MEDIUM,
	closeButtonType: CLOSE_BUTTON_TYPES.ICON,
	closeButtonText: "Close",
	ariaLabel: "Modal dialog"
};

Modal.SIZES = MODAL_SIZES;
Modal.CLOSE_BUTTON_TYPES = CLOSE_BUTTON_TYPES;

export default Modal;
